import React, { useRef, useEffect } from 'react'
import FullCalendar        from '@fullcalendar/react'
import interactionPlugin   from '@fullcalendar/interaction'
import timeGridPlugin      from '@fullcalendar/timegrid'
import scrollGrid          from '@fullcalendar/scrollgrid'

import { useCalendar }              from './useCalendar'
import { postRequest, pathname }    from '@helpers/javascript/javascript'
import { modalRequest }             from '../helpers/fetch'
import { FontAwesomeIcon }          from '@fortawesome/react-fontawesome'
import { confirmScheduleConflict }  from './conflictConfirm'

// Alternative to the overlaid "Semaine (vertical)" view: instead of stacking
// every technician's interventions on one shared week grid, each technician
// gets their own independent week planning, stacked and scrollable. All grids
// share a single date navigation.
const StackedCalendar = ({ renderEventContent, onEventDrop, onEventResize }) => {
  const { state, setState, TOOLS, filter, fetchEvents } = useCalendar()

  const apiRefs     = useRef({})
  const sectionRefs = useRef({})

  const initialDate = () => {
    const param = new URLSearchParams(window.location.search).get('selected_date')
    return param ? new Date(param) : new Date()
  }

  // The shared date lives in the calendar context so the mini-calendar keeps
  // driving navigation even without a single FullCalendar instance.
  const currentDate = state.currentDate ? new Date(state.currentDate) : initialDate()

  const technicians = TOOLS.resources
    .filter(res => res.technic && !filter.unchecked.includes(res.id))
    .sort((a, b) => (a.order - b.order) || a.title.localeCompare(b.title))

  useEffect(() => {
    if (!state.currentDate) setState(prevState => ({ ...prevState, currentDate: initialDate() }))
  }, [])

  // ── Shared week navigation: fetch the week + move every mounted grid ─────
  useEffect(() => {
    if (!state.currentDate) return

    const day   = new Date(state.currentDate)
    const start = moment(day).startOf('isoWeek').toDate()
    const end   = moment(day).startOf('isoWeek').add(7, 'days').toDate()

    setState(prevState => ({ ...prevState, startDate: start, endDate: end }))
    fetchEvents({ start, end, callback: e => setState(prevState => ({ ...prevState, events: e })) })

    Object.values(apiRefs.current).forEach(api => api && api.gotoDate(day))
  }, [state.currentDate])

  // ── Scroll straight to a technician when their name is clicked ───────────
  useEffect(() => {
    const id = state.scrollToResourceId
    if (!id) return

    const el = sectionRefs.current[id]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setState(prevState => ({ ...prevState, scrollToResourceId: null }))
  }, [state.scrollToResourceId])

  const eventsFor = techId => (state.events || [])
    .filter(event => event.resourceId != null && event.resourceId.toString() === techId.toString())
    .map(event => {
      const isAbsence = typeof event.className === 'string' && event.className.includes('absence-event')
      return isAbsence ? { ...event, backgroundColor: event.user_color } : event
    })

  const weekTitle = () => {
    const start = moment(currentDate).startOf('isoWeek')
    const end   = moment(currentDate).startOf('isoWeek').add(6, 'days')
    return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`
  }

  const goWeeks = delta => setState(prevState => ({
    ...prevState,
    currentDate: moment(prevState.currentDate || new Date()).add(delta, 'weeks').toDate()
  }))
  const goToday = () => setState(prevState => ({ ...prevState, currentDate: new Date() }))

  const handleSelect = tech => e => {
    const path   = TOOLS.workorderFromCalendar === 'modal' ? '/calendar/workorders/new' : '/workorders/new'
    const params = {
      date_planned:       e.startStr,
      predicted_duration: (e.end - e.start) / 60000,
      technician:         tech.parentId ? tech.id : "",
      manager:            tech.parentId ? tech.parentId : tech.id
    }
    const url = pathname({ path, params })
    if (TOOLS.workorderFromCalendar === 'modal') return modalRequest(url)
    window.location = url
  }

  // External drop (from the open-workorders list) lands on the technician
  // whose grid received it.
  const handleExternalDrop = tech => info => {
    const event = JSON.parse(info.draggedEl.dataset.info)

    const submit = confirmed => {
      const body = {
        event: {
          workorder_id:       event.workorder_id,
          resourceId:         tech.id,
          new_date_planned:   info.date.toISOString(),
          confirmed_conflict: confirmed
        }
      }
      postRequest({ url: pathname({ path: '/calendar/update_drop', format: 'json' }), body }).then(res => {
        if (res.conflict) {
          if (confirmScheduleConflict(res.conflict)) submit(true)
          return
        }
        if (res.unprocessed) return
        setState(prevState => {
          const openEvents = (prevState.openEvents || []).filter(wo => wo.info.workorder_id !== event.workorder_id)
          const events     = [...(prevState.events || [])]
          const idx        = events.findIndex(ev => res.event.info.workorder_id === ev.info?.workorder_id)
          if (idx !== -1) events[idx] = res.event
          else events.push(res.event)
          return {
            ...prevState,
            openEvents,
            openEventsTotal: Math.max((prevState.openEventsTotal || 0) - (((prevState.openEvents || []).length) - openEvents.length), 0),
            events
          }
        })
      })
    }

    submit(false)
  }

  return (
    <div className="calendar-stack">
      <div className="calendar-stack-toolbar">
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => goWeeks(-1)}>
            <FontAwesomeIcon icon="chevron-left" />
          </button>
          <button className="btn btn-primary" onClick={goToday}>
            {I18n.t("workorders.list_of_the_day")}
          </button>
          <button className="btn btn-primary" onClick={() => goWeeks(1)}>
            <FontAwesomeIcon icon="chevron-right" />
          </button>
        </div>
        <span className="calendar-stack-title">{weekTitle()}</span>
      </div>

      <div className="calendar-stack-scroll">
        { technicians.length === 0 &&
          <div className="calendar-stack-empty">{I18n.t("calendar.stacked_empty")}</div>
        }
        { technicians.map(tech =>
          <div
            key={tech.id}
            className="calendar-stack-tech"
            ref={el => { if (el) sectionRefs.current[tech.id] = el; else delete sectionRefs.current[tech.id] }}
          >
            <div className="calendar-stack-tech-header">
              <span className="resource-title-dot mr-1" style={{ backgroundColor: tech.color }} />
              {tech.title}
            </div>

            <FullCalendar
              ref={el => { if (el) apiRefs.current[tech.id] = el.getApi(); else delete apiRefs.current[tech.id] }}
              schedulerLicenseKey = {TOOLS.license}
              timeZone            = {TOOLS.timeZone}
              plugins             = {[interactionPlugin, timeGridPlugin, scrollGrid]}
              initialView         = "timeGridWeek"
              initialDate         = {currentDate}
              headerToolbar        = {false}
              slotMinTime         = {`${filter.slotMinTime}:00:00`}
              slotMaxTime         = {`${filter.slotMaxTime}:00:00`}
              weekends            = {filter.showWeekends}
              events              = {eventsFor(tech.id)}
              height              = "auto"
              locale              = {I18n.locale}
              eventTimeFormat     = {{ meridiem: false, hour12: false }}
              slotDuration        = "00:30:00"
              eventContent        = {renderEventContent}
              eventResize         = {onEventResize}
              eventDrop           = {onEventDrop}
              drop                = {handleExternalDrop(tech)}
              select              = {handleSelect(tech)}
              nowIndicator
              allDaySlot
              selectable
              editable
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default StackedCalendar
